<template>
  <div
    class="price-manual-list d-flex flex-column"
    :class="isMobile ? 'ga-6' : 'ga-8'"
    data-qa="lpu_page_price_manual"
  >
    <section
      v-if="specialities.length"
      id="specs"
      class="d-flex flex-column"
      :class="isMobile ? 'ga-4' : 'ga-6'"
      data-qa="lpu_page_price_specialities"
    >
      <h2
        class="text--text"
        :class="isMobile ? 'text-h6' : 'text-h5 font-weight-medium'"
      >
        {{ tStatic('Цены на приём специалистов') }}
      </h2>
      <PricesListCard borderless>
        <a
          v-for="(item, index) in specialities"
          :key="specialityId(item)"
          class="price-manual-list__row d-flex text-decoration-none rounded-20 pa-4"
          :class="[
            isMobile ? 'flex-column ga-3' : 'align-center ga-5',
            { 'bg-gray-40': index % 2 === 0 },
          ]"
          :href="specialityHref(specialityId(item))"
          data-qa="lpu_page_price_speciality_item"
        >
          <div
            class="price-manual-list__name text-body-1 primary--text"
            :class="{ 'flex-grow-1': !isMobile }"
          >
            {{ item.spec__name }}
          </div>
          <div
            class="text-subtitle-1 text--text flex-shrink-0"
            :class="{ 'text-right': !isMobile }"
          >
            {{ specialityPrice(item) }}
          </div>
        </a>
      </PricesListCard>
    </section>

    <section
      v-for="category in categories"
      :id="category.translit"
      :key="category.translit"
      class="d-flex flex-column"
      :class="isMobile ? 'ga-4' : 'ga-6'"
      data-qa="lpu_page_price_category"
    >
      <h2
        class="text--text"
        :class="isMobile ? 'text-h6' : 'text-h5'"
      >
        {{ tStatic('Цены на') }} {{ category.name }}
      </h2>
      <div
        v-if="category.url && category.name && !lpu.has_prices_with_appointment"
        class="text-body-2 text-secondary--text"
      >
        {{ tStatic('Посмотрите все цены на') }}
        <a
          class="text-decoration-underline primary--text"
          :href="category.url"
        >
          {{ category.name }}
        </a>
        , {{ tStatic('чтобы выбрать лучший вариант') }}.
      </div>
      <PriceEquipmentList :items="unwrapList(category.equipments)" />
      <div
        v-for="serviceType in unwrapList(category.service_types)"
        :key="serviceType.name || 'extra'"
        class="d-flex flex-column"
        :class="isMobile ? 'ga-3' : 'ga-4'"
      >
        <h3 class="text-subtitle-1 text--text">
          {{ serviceType.name || tStatic('Дополнительно') }}
        </h3>
        <PricesListCard borderless>
          <component
            :is="serviceHref(service) ? 'a' : 'div'"
            v-for="(service, index) in unwrapList(serviceType.services)"
            :key="`${service.name}-${index}`"
            class="price-manual-list__row d-flex text-decoration-none rounded-20"
            :class="[
              isMobile ? 'flex-column ga-3 pa-4' : 'align-center ga-8 px-4 py-3',
              { 'bg-gray-40': index % 2 === 1 },
            ]"
            :href="serviceHref(service) || undefined"
            data-qa="lpu_page_price_diagnostic_item"
          >
            <div class="price-manual-list__name text-body-1 text--text min-w-0">
              {{ service.name }}
            </div>
            <div
              class="text-subtitle-1 text--text flex-shrink-0"
              :class="{ 'text-right': !isMobile }"
            >
              {{ diagnosticPrice(service) }}
            </div>
          </component>
        </PricesListCard>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useURLStore from 'src/store/useURLStore'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import formatPrice from 'src/functions/formatPrice'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import PricesListCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesListCard.vue'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import getDiagnosticServiceHref from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/functions/getDiagnosticServiceHref'
import PriceEquipmentList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/components/PriceEquipmentList.vue'
import type {
  DiagnosticPriceCategory,
  DiagnosticService,
  SpecialityPricelistItem,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceCatalog/types'

const props = defineProps<{
  lpu: Lpu
  specialities: SpecialityPricelistItem[]
  categories: DiagnosticPriceCategory[]
}>()

const isMobile = useIsMobile()
const { reverseFull } = useURLStore()
const { context }: CommonContextStore = useContextStore()
const currency = computed(() => context.currency.symbol)

function specialityId(item: SpecialityPricelistItem) {
  return item.spec__id ?? item.spec_id ?? 0
}

function specialityHref(specId: number) {
  const doctorsUrl = reverseFull({
    name: 'lpu_doctors',
    args: [
      props.lpu.town.translit,
      props.lpu.translit,
    ],
  })

  return `${doctorsUrl}#speciality=${specId}`
}

function specialityPrice(item: SpecialityPricelistItem) {
  if (Number(item.max_price) === 0) {
    return tStatic('бесплатно')
  }

  return `${tStatic('от')} ${formatPrice({
    price: item.min_price,
    currencySymbol: currency.value,
  })}`
}

function diagnosticPrice(service: DiagnosticService) {
  return `${tStatic('от')} ${formatPrice({
    price: service.price,
    currencySymbol: currency.value,
  })}`
}

function serviceHref(service: DiagnosticService) {
  return getDiagnosticServiceHref(
    context.town.translit,
    service.group,
    service.translit,
  )
}
</script>

<style scoped lang="scss">
@import 'src/styles/variables';

.price-manual-list {
  &__row {
    color: inherit;

    &:hover {
      background-color: $ui-kit-bg-gray-40;
    }
  }

  &__name {
    min-width: 0;
    word-break: break-word;
  }
}
</style>
